import React, { useEffect, useState } from "react";
import { X, BarChart3, Phone, Music, DollarSign, ChevronRight, Clock, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import api, { formatApiError } from "../lib/api";

/**
 * RadioShowHistoryModal — host-only post-show analytics.
 *
 * Two views:
 *   1. List view  — last N sessions with summary metrics (callers, requests, tips)
 *   2. Detail view — clicking a session shows full caller list, song
 *                    requests, and tips for that session, plus charts:
 *                      • Caller-status breakdown (bar)
 *                      • Request-status mix (pie)
 *                      • Tips amount over time (bar histogram)
 *
 * Each row also exposes a one-tap CSV export — backend route
 * `/api/radio/host/<slug>/show-history/<id>/export.csv` produces a single
 * rectangular sheet ready for sponsor / accounting use.
 */
const STATUS_COLORS = {
  onair: "#ef4444", private: "#f59e0b", waiting: "#10b981",
  done: "#64748b", cancelled: "#475569", rejected: "#dc2626",
  accepted: "#10b981", played: "#fbbf24", pending: "#94a3b8",
};

async function downloadShowCsv(stationSlug, sessionId) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/radio/host/${stationSlug}/show-history/${sessionId}/export.csv`;
  try {
    const r = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("tv_token") || ""}` },
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `tapin-radio-${stationSlug}-${sessionId}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(blobUrl);
    toast.success("Show CSV downloaded");
  } catch (e) {
    toast.error("Couldn't download show CSV");
  }
}

export default function RadioShowHistoryModal({ stationSlug, onClose }) {
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!stationSlug) return;
    api.get(`/radio/host/${stationSlug}/show-history?limit=20`)
      .then((r) => setList(r.data?.items || []))
      .catch((e) => { toast.error(formatApiError(e)); setList([]); });
  }, [stationSlug]);

  const openDetail = async (sessionId) => {
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/radio/host/${stationSlug}/show-history/${sessionId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoadingDetail(false); }
  };

  return (
    <div
      data-testid="radio-show-history-modal"
      className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-3xl max-h-[88vh] bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-gradient-to-r from-fuchsia-500/10 via-rose-500/10 to-amber-500/10">
          <div className="flex items-center gap-2">
            {detail && (
              <button data-testid="radio-history-back" onClick={() => setDetail(null)} className="text-white/60 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <BarChart3 className="w-4 h-4 text-tv-accent" />
            <div className="font-display font-bold text-white text-sm">
              {detail ? "Show analytics" : "Show history"}
            </div>
          </div>
          <button data-testid="radio-history-close" onClick={onClose} className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!detail && (
            <ListView list={list} loadingDetail={loadingDetail} onOpen={openDetail} stationSlug={stationSlug} />
          )}
          {detail && <DetailView detail={detail} stationSlug={stationSlug} />}
        </div>
      </div>
    </div>
  );
}

function ListView({ list, loadingDetail, onOpen, stationSlug }) {
  if (list === null) return <div className="text-white/40 text-sm">Loading…</div>;
  if (list.length === 0) return <div className="text-white/40 text-sm">No shows yet — go live to start collecting analytics.</div>;
  return (
    <div className="space-y-2" data-testid="radio-history-list">
      {list.map((s) => (
        <div
          key={s.id}
          data-testid={`radio-history-row-${s.id}`}
          className="w-full bg-white/5 hover:bg-white/10 rounded-lg flex items-center gap-1 transition group"
        >
          <button
            onClick={() => onOpen(s.id)}
            disabled={loadingDetail}
            className="flex-1 text-left p-3 flex items-center gap-3 disabled:opacity-50"
            data-testid={`radio-history-row-open-${s.id}`}
          >
            <Clock className="w-4 h-4 text-white/40" />
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold text-sm">
                {fmtDate(s.started_at)}
                {s.is_live && <span className="ml-2 text-[9px] uppercase tracking-wider font-black bg-red-600 text-white px-1.5 py-0.5 rounded animate-pulse">live now</span>}
              </div>
              <div className="text-white/40 text-[11px]">{durationStr(s.started_at, s.ended_at)}</div>
            </div>
            <Tile icon={<Phone className="w-3.5 h-3.5" />} label="Callers" value={s.caller_total} />
            <Tile icon={<Music className="w-3.5 h-3.5" />} label="Requests" value={s.requests_total} />
            <Tile icon={<DollarSign className="w-3.5 h-3.5" />} label="Tips" value={`$${(s.tips_total || 0).toFixed(0)}`} />
            <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/70" />
          </button>
          <button
            data-testid={`radio-history-row-csv-${s.id}`}
            onClick={(e) => { e.stopPropagation(); downloadShowCsv(stationSlug, s.id); }}
            title="Export this show as CSV — sponsor / accounting ready"
            className="p-2 mr-1 rounded text-white/40 hover:text-tv-accent hover:bg-white/5 transition"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function DetailView({ detail, stationSlug }) {
  const { session, callers, requests, tips } = detail;
  const tipsTotal = tips.reduce((acc, t) => acc + (t.amount || 0), 0);

  // Caller-status breakdown — bar chart. We bucket "done" + "onair" as
  // "On-air" (since done = was on-air, then ended) so the host sees a
  // single "made it on" number rather than splitting current-vs-past.
  const callerCounts = callers.reduce((m, c) => {
    const k = (c.status === "done" || c.status === "onair") ? "On-air"
            : c.status === "private" ? "Private"
            : c.status === "waiting" ? "Waiting"
            : "Dropped";
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {});
  const callerData = Object.entries(callerCounts).map(([name, value]) => ({ name, value }));

  // Request-status mix — pie. Hosts care: how many requests landed?
  const requestCounts = requests.reduce((m, q) => {
    m[q.status || "pending"] = (m[q.status || "pending"] || 0) + 1; return m;
  }, {});
  const requestData = Object.entries(requestCounts).map(([name, value]) => ({ name, value }));

  // Tips over time — bar histogram by 5-minute bucket relative to show start.
  const startMs = session.started_at ? new Date(session.started_at).getTime() : 0;
  const tipBuckets = {};
  for (const t of tips) {
    const ms = t.created_at ? new Date(t.created_at).getTime() : 0;
    const bucketMin = startMs ? Math.floor((ms - startMs) / 60000 / 5) * 5 : 0;
    tipBuckets[bucketMin] = (tipBuckets[bucketMin] || 0) + (t.amount || 0);
  }
  const tipData = Object.entries(tipBuckets)
    .map(([m, v]) => ({ name: `+${m}m`, value: Number(v.toFixed(2)) }))
    .sort((a, b) => parseInt(a.name) - parseInt(b.name));

  return (
    <div className="space-y-5" data-testid="radio-history-detail">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-white font-display font-bold text-lg">{fmtDate(session.started_at)}</div>
        <div className="text-white/50 text-sm">· {durationStr(session.started_at, session.ended_at)}</div>
        {session.is_live && <span className="text-[9px] uppercase tracking-wider font-black bg-red-600 text-white px-1.5 py-0.5 rounded animate-pulse">live now</span>}
        <button
          data-testid="radio-history-detail-csv"
          onClick={() => downloadShowCsv(stationSlug, session.id)}
          title="Export this show as CSV"
          className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-black border rounded px-2 py-1 transition bg-white/5 text-white/70 border-white/15 hover:text-white hover:bg-white/10"
        >
          <Download className="w-3 h-3" /> Export CSV
        </button>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-3 gap-2">
        <BigTile label="Callers" value={callers.length} />
        <BigTile label="Requests" value={requests.length} />
        <BigTile label="Tips total" value={`$${tipsTotal.toFixed(2)}`} />
      </div>

      {/* Charts row — only renders columns that have data so empty
          shows still look clean. */}
      {(callerData.length > 0 || requestData.length > 0 || tipData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="radio-history-charts">
          {callerData.length > 0 && (
            <ChartCard title="Caller funnel" testid="radio-history-chart-callers">
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={callerData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,.1)", fontSize: 11 }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {callerData.map((d, i) => (
                      <Cell key={i} fill={
                        d.name === "On-air" ? "#10b981"
                        : d.name === "Private" ? "#f59e0b"
                        : d.name === "Waiting" ? "#06b6d4"
                        : "#ef4444"
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          {requestData.length > 0 && (
            <ChartCard title="Request mix" testid="radio-history-chart-requests">
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={requestData} dataKey="value" nameKey="name" outerRadius={50} innerRadius={28}>
                    {requestData.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.name] || "#94a3b8"} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,.1)", fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10, color: "#94a3b8" }} iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          {tipData.length > 0 && (
            <ChartCard title="Tips over time" testid="radio-history-chart-tips">
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={tipData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(v) => `$${Number(v).toFixed(2)}`}
                    contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,.1)", fontSize: 11 }}
                  />
                  <Bar dataKey="value" fill="#facc15" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      <Section title={`Callers (${callers.length})`} testid="radio-history-callers">
        {callers.length === 0
          ? <Empty>No callers in this session.</Empty>
          : callers.map((c) => (
            <Row
              key={c.id}
              avatar={c.user_avatar}
              title={c.user_name}
              subtitle={`${c.city || "—"} · ${c.topic || "no topic"}`}
              right={<StatusPill status={c.status} />}
            />
          ))}
      </Section>

      <Section title={`Song requests (${requests.length})`} testid="radio-history-requests">
        {requests.length === 0
          ? <Empty>No song requests.</Empty>
          : requests.map((q) => (
            <Row
              key={q.id}
              title={`${q.title}${q.artist ? ` — ${q.artist}` : ""}`}
              subtitle={`${q.user_name}${q.note ? ` · "${q.note}"` : ""}`}
              right={<StatusPill status={q.status} />}
            />
          ))}
      </Section>

      <Section title={`Tips (${tips.length}) · $${tipsTotal.toFixed(2)}`} testid="radio-history-tips">
        {tips.length === 0
          ? <Empty>No tips this session.</Empty>
          : tips.map((t) => (
            <Row
              key={t.id}
              avatar={t.tipper_avatar}
              title={t.tipper_name || "Listener"}
              subtitle={t.message ? `"${t.message}"` : "—"}
              right={<span className="text-tv-accent font-mono font-black">${(t.amount || 0).toFixed(2)}</span>}
            />
          ))}
      </Section>
    </div>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const durationStr = (start, end) => {
  if (!start) return "—";
  try {
    const s = new Date(start).getTime();
    const e = end ? new Date(end).getTime() : Date.now();
    const mins = Math.max(0, Math.round((e - s) / 60000));
    if (mins < 60) return `${mins} min`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  } catch { return "—"; }
};

function Tile({ icon, label, value }) {
  return (
    <div className="hidden sm:flex flex-col items-center px-2 py-1 min-w-[64px]">
      <div className="text-white/50 inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider">{icon} {label}</div>
      <div className="text-white font-display font-black text-base tabular-nums">{value}</div>
    </div>
  );
}

function BigTile({ label, value }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
      <div className="text-white/50 text-[10px] uppercase font-bold tracking-wider">{label}</div>
      <div className="text-white font-display font-black text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid} className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{title}</div>
      <div className="space-y-1.5 bg-black/30 rounded-lg p-2">{children}</div>
    </div>
  );
}

function ChartCard({ title, testid, children }) {
  return (
    <div data-testid={testid} className="bg-white/5 border border-white/10 rounded-lg p-2.5">
      <div className="text-[9px] uppercase tracking-wider text-white/50 font-bold mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function Row({ avatar, title, subtitle, right }) {
  return (
    <div className="flex items-center gap-2 bg-white/5 rounded p-2">
      {avatar ? <img src={avatar} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-white/10" />}
      <div className="flex-1 min-w-0">
        <div className="text-white text-xs font-bold truncate">{title}</div>
        <div className="text-white/50 text-[10px] truncate">{subtitle}</div>
      </div>
      {right}
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    onair:     "bg-red-600 text-white",
    private:   "bg-amber-500 text-black",
    waiting:   "bg-emerald-500 text-black",
    done:      "bg-white/10 text-white/70",
    cancelled: "bg-white/5 text-white/40 line-through",
    rejected:  "bg-rose-500/20 text-rose-200",
    accepted:  "bg-emerald-500/20 text-emerald-200",
    played:    "bg-tv-accent text-black",
    pending:   "bg-white/10 text-white/70",
  };
  return (
    <span className={`text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded ${styles[status] || "bg-white/10 text-white/70"}`}>
      {status}
    </span>
  );
}

function Empty({ children }) {
  return <div className="text-white/40 text-xs italic px-1 py-2">{children}</div>;
}
